import { useMemo } from 'react'
import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three-stdlib'
import type { TruckKleuren } from './onderdelen'

/**
 * Bouwdelen voor de Matics die naar fabrieksbeelden zijn gebouwd (K-Matic,
 * R-Matic). Ze delen materialen en een paar vormen: dezelfde lak, hetzelfde
 * PU van de wielen, dezelfde Balyo-module. Gedeelde materialen zijn ook
 * goedkoper — een shader per materiaal, niet per onderdeel.
 */

export type V3 = [number, number, number]

/** Geel-zwarte arcering voor de hoeken van het chassis. */
function streepTextuur() {
  const c = document.createElement('canvas')
  c.width = 64
  c.height = 64
  const g = c.getContext('2d')
  if (!g) return null
  g.fillStyle = '#16171a'
  g.fillRect(0, 0, 64, 64)
  g.fillStyle = '#f0c419'
  for (let i = -64; i < 128; i += 22) {
    g.beginPath()
    g.moveTo(i, 0)
    g.lineTo(i + 11, 0)
    g.lineTo(i + 11 - 64, 64)
    g.lineTo(i - 64, 64)
    g.closePath()
    g.fill()
  }
  const t = new THREE.CanvasTexture(c)
  t.colorSpace = THREE.SRGBColorSpace
  return t
}

export function useMaticMaterialen(kleuren: TruckKleuren) {
  return useMemo(() => {
    // Industriële lak, geen autolak (21 oktober). Met een volle blanke laag
    // (clearcoat 1, ruwheid 0,07) spiegelde elk paneel de lichtbakken als een
    // nieuwe auto in de showroom — dat glimmende was een groot deel van de
    // "AI-look". Een truck in een hal is zijdeglans.
    const lakRood = new THREE.MeshPhysicalMaterial({
      color: kleuren.rood, roughness: 0.42, metalness: 0.04, clearcoat: 0.5, clearcoatRoughness: 0.22,
    })
    // Mast, chassis en vorken zijn gespoten staal: poedercoating, en dat is
    // een diëlektricum — geen metaal. Met metalness 0,4 verloren ze de helft
    // van hun diffuse kleur en werden ze zwart zodra er niets te spiegelen
    // viel; op de draaitafels van Linde is de mast middengrijs [73,77,80]
    // naast lak van [212,19,19]. Dezelfde verhouding geeft hier #474a50.
    const mast = new THREE.MeshPhysicalMaterial({
      color: '#474a50', roughness: 0.5, metalness: 0.05, clearcoat: 0.25, clearcoatRoughness: 0.35,
    })
    const chassis = new THREE.MeshStandardMaterial({ color: '#393b40', roughness: 0.55, metalness: 0.05 })
    const kunststof = new THREE.MeshStandardMaterial({ color: kleuren.zwart, roughness: 0.52, metalness: 0.04 })
    const zwart = new THREE.MeshStandardMaterial({ color: '#141519', roughness: 0.7, metalness: 0.1 })
    const pu = new THREE.MeshStandardMaterial({ color: '#b48d5e', roughness: 0.62, metalness: 0.02 })
    const geel = new THREE.MeshStandardMaterial({ color: '#f2c200', roughness: 0.45, metalness: 0.05 })
    // Een noodstop, een scherm en een lidarvenster gloeien niet: met de oude
    // emissie bloomden ze als lampjes, en zo'n gloed op elk klein onderdeel is
    // wat een render verraadt. Het scherm geeft nog licht, maar zoals een
    // display overdag; het lidarvenster is donker glas.
    const noodstop = new THREE.MeshStandardMaterial({ color: '#d4101c', roughness: 0.35 })
    const scherm = new THREE.MeshStandardMaterial({
      color: '#7ce08a', emissive: '#5fd070', emissiveIntensity: 0.4, roughness: 0.2,
    })
    const lens = new THREE.MeshStandardMaterial({ color: '#0b1016', roughness: 0.12, metalness: 0.6 })
    const vork = new THREE.MeshStandardMaterial({ color: '#3b3d42', roughness: 0.45, metalness: 0.12 })
    // Vizier en cabineruit: donker, glad, met een spiegeling — geen zwart vlak.
    const glas = new THREE.MeshPhysicalMaterial({
      color: '#10141b', roughness: 0.06, metalness: 0.1, clearcoat: 1, clearcoatRoughness: 0.03,
      transparent: true, opacity: 0.82,
    })
    const streep = new THREE.MeshStandardMaterial({ map: streepTextuur(), roughness: 0.55 })
    // Het zwaaivlak is grijs gespoten, lichter dan de mast, met dezelfde lak.
    const zwaai = new THREE.MeshPhysicalMaterial({
      color: '#595c63', roughness: 0.4, metalness: 0.2, clearcoat: 0.35, clearcoatRoughness: 0.3,
    })
    // Binnenmast: dieper in de schaduw dan de buitenkant, zodat de
    // uitsparingen als gat lezen en niet als vlek.
    const binnen = new THREE.MeshStandardMaterial({ color: '#26282c', roughness: 0.6, metalness: 0.05 })
    return { lakRood, mast, chassis, kunststof, zwart, pu, geel, noodstop, scherm, lens, vork, glas, streep, zwaai, binnen }
  }, [kleuren.rood, kleuren.zwart])
}

export type Mat = ReturnType<typeof useMaticMaterialen>

/**
 * Een doos met afgeronde ribben, gedeeld per maat.
 *
 * Een ribbe van nul millimeter bestaat niet: gezet plaatwerk heeft een
 * buigradius, spuitgietwerk een afronding, een gezaagd profiel een braam.
 * Die paar millimeter vangen het licht — een dunne glimlijn langs elke rand —
 * en juist het ontbreken daarvan is waar het oog een render aan herkent.
 * Op de Linde-draaitafels zie je die lijn langs elk paneel.
 *
 * Straal: 6 mm, of een kwart van de kleinste maat bij dunne delen; onder de
 * 4 mm blijft het een gewone doos. Grote volumes — chassis, kappen, accubak,
 * alles van 10 cm en dikker — krijgen 12 mm (21 oktober): op 10 m is een
 * pixel bij 1080p hier 7 mm, dus 6 mm is net één pixel en 12 mm een lijn.
 * Profielen en platen houden 6 mm; daar zou meer zacht ogen, niet echt.
 * Eén segment per ronding — een schuine kant, 108 driehoeken per doos in
 * plaats van 300 bij twee — vangt het licht al; meer segmenten zie je op
 * kioskafstand niet, en onder de softwarerenderer van de schermafdrukken
 * kostte het de hotspotwandeling haar tijdslimiet. Elke maat bestaat maar
 * één keer.
 */
const dozen = new Map<string, THREE.BufferGeometry>()
function doos(b: number, h: number, d: number) {
  const sleutel = `${b.toFixed(4)}|${h.toFixed(4)}|${d.toFixed(4)}`
  let g = dozen.get(sleutel)
  if (!g) {
    const klein = Math.min(b, h, d)
    g = klein < 0.004
      ? new THREE.BoxGeometry(b, h, d)
      : new RoundedBoxGeometry(b, h, d, 1, klein >= 0.1 ? 0.012 : Math.min(0.006, klein * 0.25))
    dozen.set(sleutel, g)
  }
  return g
}

/** Doos met een gedeeld materiaal: [x, y, z] midden, [b, h, d] maat. */
export function Blok({ p, s, m, schaduw = true }: { p: V3; s: V3; m: THREE.Material; schaduw?: boolean }) {
  const g = useMemo(() => doos(s[0], s[1], s[2]), [s[0], s[1], s[2]])
  return <mesh position={p} geometry={g} material={m} castShadow={schaduw} receiveShadow />
}

/** Doos tussen twee hoekpunten — leest makkelijker naast de maten uit de beelden. */
export function Tussen({ a, b, m, schaduw = true }: { a: V3; b: V3; m: THREE.Material; schaduw?: boolean }) {
  return (
    <Blok
      p={[(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2]}
      s={[Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1]), Math.abs(b[2] - a[2])]}
      m={m} schaduw={schaduw}
    />
  )
}

/** PU-wiel of geleiderol: de lichtbruine polyurethaan die op de beelden opvalt. */
export function PuWiel({ p, r, b, m, staand = true }: { p: V3; r: number; b: number; m: Mat; staand?: boolean }) {
  return (
    <group position={p} rotation={staand ? [0, 0, Math.PI / 2] : [0, 0, 0]}>
      <mesh material={m.pu} castShadow>
        <cylinderGeometry args={[r, r, b, 18]} />
      </mesh>
      <mesh material={m.chassis}>
        <cylinderGeometry args={[r * 0.42, r * 0.42, b + 0.01, 10]} />
      </mesh>
    </group>
  )
}

/**
 * "DRIVEN by Balyo" als canvas-textuur op de Balyo-module. Het vlak kijkt
 * naar −z; `rotatie` draait het naar een andere kant.
 */
export function DrivenOpschrift({ positie, rotatie = [0, Math.PI, 0], breedte = 0.34 }: {
  positie: V3
  rotatie?: V3
  breedte?: number
}) {
  const tex = useMemo(() => {
    const c = document.createElement('canvas')
    c.width = 256
    c.height = 64
    const g = c.getContext('2d')
    if (!g) return null
    g.font = '600 34px Inter, Arial, sans-serif'
    g.textBaseline = 'middle'
    // Brede spatiëring, zoals het woordmerk op de machine.
    ;['D', 'R', 'I', 'V', 'E', 'N'].forEach((l, i) => {
      g.fillStyle = l === 'V' ? '#e2362f' : '#e9ebee'
      g.fillText(l, 8 + i * 40, 26)
    })
    g.fillStyle = '#b8bcc4'
    g.font = '500 13px Inter, Arial, sans-serif'
    g.fillText('BY BALYO', 150, 52)
    const t = new THREE.CanvasTexture(c)
    t.colorSpace = THREE.SRGBColorSpace
    return t
  }, [])
  if (!tex) return null
  return (
    <mesh position={positie} rotation={rotatie}>
      <planeGeometry args={[breedte, breedte / 4]} />
      <meshStandardMaterial map={tex} transparent roughness={0.5} />
    </mesh>
  )
}
